import { RULE_ACTION_OPTIONS, describeCondition, ruleActionLabel, ruleDisplayName } from "../lib/rule-action";
import type { Rule, RuleAction } from "../lib/rules";

const BADGE_TONE: Record<RuleAction, string> = {
  allow: "border-success bg-success-bg text-success",
  reject: "border-danger bg-danger-bg text-danger",
  review: "border-border-primary bg-bg-tertiary text-text-secondary",
};

export function RuleActionBadge({ action }: { action: RuleAction }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${BADGE_TONE[action]}`}
    >
      {ruleActionLabel(action)}
    </span>
  );
}

/** Radio group to pick what a rule does. Plain function (no hooks). */
export function RuleActionField({
  value,
  onChange,
  disabled,
}: {
  value: RuleAction;
  onChange: (action: RuleAction) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5" role="radiogroup" aria-label="Art der Regel">
      {RULE_ACTION_OPTIONS.map((o) => (
        <label key={o.value} className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="rule-action"
            value={o.value}
            checked={value === o.value}
            disabled={disabled}
            onChange={() => onChange(o.value)}
            className="mt-1"
          />
          <span>
            <span className="font-medium text-text-primary">{o.label}</span>
            <span className="block text-xs text-text-tertiary">{o.hint}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

/** One rule as a line: kind, name, shipped marker, event type and condition. */
export function RuleSummary({ rule }: { rule: Rule }) {
  return (
    <>
      <RuleActionBadge action={rule.action} />
      <span className="text-xs font-medium text-text-primary">
        {ruleDisplayName(rule)}
      </span>
      {rule.builtin && (
        <span className="rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
          mitgeliefert
        </span>
      )}
      <span className="font-mono text-xs text-text-primary">{rule.event_type}</span>
      <span className="flex-1 truncate font-mono text-[11px] text-text-secondary">
        {describeCondition(rule.condition)}
      </span>
    </>
  );
}

/** Plain-text reason a save was refused; renders nothing without a message. */
export function RuleConflictNotice({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p
      role="alert"
      className="rounded-md border border-danger bg-danger-bg px-3 py-2 text-sm text-text-primary"
    >
      {message}
    </p>
  );
}
